import { Injectable, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ComponentStore, OnStoreInit } from '@ngrx/component-store';
import { concatLatestFrom, tapResponse } from '@ngrx/operators';
import { createSelector, Store } from '@ngrx/store';
import { formatISO } from 'date-fns';
import { Observable, exhaustMap, filter, map, of, startWith, tap } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { CompanyLanguage } from '@ninety/mobile/platform/api';
import {
  CurrentUserTeamSelectors,
  LanguageSelectors,
  NotificationActions,
  UniversalCreateActions,
} from '@ninety/mobile/platform/state';

import { AssessmentDefinitionsResponse } from '../../../models/assessment-definitions-response.model';
import { CompanyAssessmentListItemResponse } from '../../../models/company-assessment-list-item-response.model';
import { CreateCompanyAssessmentRequest } from '../../../models/create-company-assessment-request.model';
import {
  CompanyAssessmentMetadata,
  CompanyAssessmentMetadataForm,
} from '../../../models/form/company-assessment-metadata-form.model';
import { CreateAssessmentForm } from '../../../models/form/create-assessment-form.model';
import { AssessmentSanitizerService } from '../../../services/assessment-sanitizer.service';
import { SharedAssessmentApiService } from '../../../services/shared-assessment-api.service';

import { CreateAssessmentDialogStoreActions } from './create-assessment-dialog.actions';
import { CreateAssessmentDialogState } from './create-assessment-dialog.model';

@Injectable()
export class CreateAssessmentDialogStore extends ComponentStore<CreateAssessmentDialogState> implements OnStoreInit {
  private readonly assessmentsApiService = inject(SharedAssessmentApiService);
  private readonly store = inject(Store);
  private readonly sanitizer = inject(AssessmentSanitizerService);

  readonly form = new FormGroup<CreateAssessmentForm>({
    assessmentMetadata: new FormGroup<CompanyAssessmentMetadataForm>({
      assessmentDefinitionId: new FormControl<string>('', Validators.required),
      name: new FormControl<string>(''),
      description: new FormControl<string | null>(''),
      launchDate: new FormControl<Date | string>(formatISO(new Date()), Validators.required),
      endDate: new FormControl<Date | string>(formatISO(new Date()), Validators.required),
    }),
    teamIds: new FormControl([], Validators.required),
    createTodos: new FormControl<boolean>(false),
    // We currently don't allow users to change this, however, this will be used in the future.
    // For now, it is not bound to any formControl/formControlName directive in the template.
    anonymousReplies: new FormControl(false),
    sendReportToUserIds: new FormControl([]),
  });

  readonly language$ = this.store.select(LanguageSelectors.language);
  readonly teams$ = this.store.select(CurrentUserTeamSelectors.selectTeams);

  selectAssessmentsUniversalCreateButtonText = createSelector(
    LanguageSelectors.assessmentsLanguage,
    lang => `Create ${lang.item}`
  );

  // Based on the launchDate, we display the string 'Schedule' or 'Launch' on the call to action
  readonly ctaText$ = this.form.controls.assessmentMetadata.valueChanges.pipe(
    map(val => {
      const launchDate = val?.launchDate ? new Date(val.launchDate).valueOf() : null;
      if (launchDate) {
        return launchDate > new Date().valueOf() ? 'Schedule' : 'Launch';
      } else {
        return 'Launch';
      }
    }),
    startWith('Launch')
  );

  readonly assessmentDefinitionSelectOptions$: Observable<any[]> = this.select(
    this.select(state => state.assessmentDefinitions),
    definitions => definitions.map(def => ({ value: def.assessmentDefinitionId, text: def.name }))
  );

  // readonly isLoading$ = this.store.select(state => state);

  readonly selectedAssessmentDefinition$ = this.select(state =>
    state.assessmentDefinitions.find(survey => {
      const assessmentMetadata = this.form.getRawValue().assessmentMetadata;
      return assessmentMetadata ? survey.assessmentDefinitionId === assessmentMetadata.assessmentDefinitionId : false;
    })
  );

  ngrxOnStoreInit() {
    this.initStore();
  }

  // Load Teams and Users to populate available options for form controls
  readonly initStore = this.effect((void$: Observable<void>) =>
    void$.pipe(
      concatLatestFrom(() => this.language$),
      switchMap(([_, language]) =>
        this.assessmentsApiService.getAssessmentDefinitions().pipe(
          tapResponse({
            next: assessmentDefinitions => this.setState({ assessmentDefinitions }),
            error: error => {
              this.logError(
                error,
                `Unable to load ${language.survey.item.toLowerCase()} details. Please refresh and try again.`
              );
            },
          })
        )
      )
    )
  );

  readonly createCompanyAssessment = this.effect((void$: Observable<void>) =>
    void$.pipe(
      filter(() => this.form.valid),
      tap(() => {
        this.form.disable();
      }),
      concatLatestFrom(() => this.selectedAssessmentDefinition$),
      // Set the value of the "name" FormControl if the user did not enter a value
      tap(([_, selectedAssessmentDefinition]) => {
        if (selectedAssessmentDefinition) {
          this.setAssessmentName(selectedAssessmentDefinition);
        }
      }),
      map(() => this.buildCreateCompanyAssessmentDto()),
      concatLatestFrom(() => [this.language$]),
      exhaustMap(([dto, language]) =>
        this.assessmentsApiService.createCompanyAssessment(dto).pipe(
          tap(companyAssessment => {
            this.addSurveyToListView(language);
            this.announceCreated(companyAssessment);
            this.store.dispatch(UniversalCreateActions.close({ dispatchSource: 'Create Assessment Dialog Store' }));
          }),
          catchError((error: unknown) =>
            of(error).pipe(
              tap(error => {
                this.logError(error, `Unable to create ${language.survey.item}. Please try again.`);
              })
            )
          )
        )
      )
    )
  );

  constructor() {
    super({ assessmentDefinitions: [] });
  }

  private buildCreateCompanyAssessmentDto(): CreateCompanyAssessmentRequest {
    const formValue = this.form.getRawValue();
    const requestModel: CreateCompanyAssessmentRequest = {
      assessmentDefinitionId: formValue.assessmentMetadata?.assessmentDefinitionId ?? '',
      name: formValue.assessmentMetadata?.name ?? '',
      launchDate: formValue.assessmentMetadata?.launchDate
        ? formValue.assessmentMetadata.launchDate
        : formatISO(new Date()),
      endDate: formValue.assessmentMetadata?.endDate ? formValue.assessmentMetadata.endDate : formatISO(new Date()),
      description: this.sanitizer.getSanitizedDescription(formValue.assessmentMetadata?.description ?? ''),
      teamIds: formValue.teamIds ?? [],
      createTodos: formValue.createTodos ?? false,
      anonymousReplies: formValue.anonymousReplies ?? false,
      sendReportToUserIds: formValue.sendReportToUserIds ?? [],
    };
    return requestModel;
  }

  private logError(error: any, message: string) {
    this.store.dispatch(NotificationActions.notify({ message }));
  }

  // Set the value of the "name" FormControl if the user did not enter a value
  private setAssessmentName(assessmentDefinition: AssessmentDefinitionsResponse) {
    const formValue = this.form.getRawValue().assessmentMetadata;
    if (!formValue?.name?.trim()) {
      this.form.controls.assessmentMetadata.patchValue({
        ...(this.form.controls.assessmentMetadata.value as CompanyAssessmentMetadata),
        name: assessmentDefinition.name,
      });
    }
  }

  // Called only after a successful creation of a company survey
  private addSurveyToListView(language: CompanyLanguage) {
    this.store.dispatch(NotificationActions.notify({ message: language.survey.item + ' created successfully' }));
  }

  // Broadcast the created assessment so analytics (and any future listeners) can react.
  private announceCreated(companyAssessment: CompanyAssessmentListItemResponse) {
    this.store.dispatch(CreateAssessmentDialogStoreActions.createCompanyAssessmentSuccess({ companyAssessment }));
  }
}
